/**
 * @fileoverview `<OperatorLogo>` — logo d'un opérateur mobile money CI.
 *
 * Affiche le logo officiel de l'opérateur à partir de son code OpenAPI
 * (`OperatorCode` : OM_CI, MTN_CI, MOOV_CI, WAVE_CI). Les assets vivent
 * dans `public/operators/<CODE>.svg` de chaque app consommatrice — le
 * composant référence `${BASE_URL}operators/…` pour rester correct quand
 * l'app est servie depuis un sous-chemin (ex. console sous `/_/`).
 *
 * Usage GNPC : colonnes opérateur des tables backoffice (PaymentsList),
 * légendes de répartition du Dashboard, détail transaction. Le PORTAIL
 * citoyen n'affiche JAMAIS de sélecteur d'opérateur (le choix se fait sur
 * le guichet Paymetrust) — ce composant est donc console-only en V1.
 *
 * Note métier : `MOOV_CI` est servi par le logo Flooz — Flooz est la
 * marque mobile money de Moov Africa en Côte d'Ivoire.
 *
 * @see Documents/openapi.yaml — schéma `OperatorCode`
 */

import { cn } from '../lib/utils.ts';

/**
 * Codes opérateur alignés sur l'enum OpenAPI `OperatorCode`.
 * `null` (transaction sans opérateur connu — usager pas encore passé
 * au guichet) est géré par le fallback.
 */
export type OperatorCode = 'OM_CI' | 'MTN_CI' | 'MOOV_CI' | 'WAVE_CI';

/**
 * Libellés humains FR — utilisés en `alt` et dans les tooltips.
 * Centralisés ici pour rester cohérents entre toutes les vues.
 */
export const OPERATOR_LABELS: Record<OperatorCode, string> = {
  OM_CI: 'Orange Money',
  MTN_CI: 'MTN Money',
  MOOV_CI: 'Moov Money',
  WAVE_CI: 'Wave',
};

export interface OperatorLogoProps {
  /** Code opérateur OpenAPI, ou `null`/`undefined` si inconnu. */
  code: OperatorCode | null | undefined;
  /**
   * Taille du logo (carré). `sm` = 20 px (cellules de table),
   * `md` = 28 px (défaut, cartes et légendes), `lg` = 40 px (détail).
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Affiche le libellé de l'opérateur à droite du logo.
   * Pratique en colonne de table (`<OperatorLogo code={op} withLabel />`).
   */
  withLabel?: boolean;
  /** Classe supplémentaire sur le conteneur. */
  className?: string;
}

const SIZE_CLASSES: Record<NonNullable<OperatorLogoProps['size']>, string> = {
  sm: 'size-5',
  md: 'size-7',
  lg: 'size-10',
};

/**
 * Logo opérateur mobile money avec fallback pour code inconnu.
 *
 * @example
 * ```tsx
 * <OperatorLogo code="OM_CI" withLabel />          // Orange Money + logo
 * <OperatorLogo code={payment.operatorCode} size="sm" />
 * <OperatorLogo code={null} withLabel />           // fallback "—"
 * ```
 */
export function OperatorLogo({
  code,
  size = 'md',
  withLabel = false,
  className,
}: OperatorLogoProps): React.JSX.Element {
  // Opérateur inconnu : transaction initiée mais usager pas encore passé
  // au guichet (operator_code nullable dans l'OpenAPI). Rendu neutre,
  // jamais une erreur.
  if (!code) {
    return (
      <span
        className={cn('inline-flex items-center gap-2 text-muted-foreground', className)}
      >
        <span
          aria-hidden="true"
          className={cn(
            'inline-flex items-center justify-center rounded-full bg-muted font-mono text-[10px]',
            SIZE_CLASSES[size],
          )}
        >
          ?
        </span>
        {withLabel && <span className="text-sm">Non renseigné</span>}
      </span>
    );
  }

  const label = OPERATOR_LABELS[code];

  return (
    <span className={cn('inline-flex items-center gap-2', className)}>
      <img
        src={`${import.meta.env.BASE_URL}operators/${code}.svg`}
        alt={withLabel ? '' : label}
        aria-hidden={withLabel ? 'true' : undefined}
        className={cn('shrink-0 rounded-full object-contain', SIZE_CLASSES[size])}
      />
      {withLabel && <span className="text-sm text-foreground">{label}</span>}
    </span>
  );
}
